import { type BetslipStateData, type EntitiesBetslips } from '../../../states/entities/betslips';
import { type BetslipState } from '../../../states/entities/betslips';
import { type StateType } from '../../../states/index';
import { type BetslipStatusType } from '../../../types/history';
import { type Selector } from '../../../types/selectors';
import { getBetslipIdFromProps } from '../../_propsGetters_';
import { selectEntitiesState } from '../entitiesPrimary.selector';

const emptyObject = {};

export const selectEntitiesBetslips: Selector<EntitiesBetslips> = (state: StateType) =>
  selectEntitiesState(state).betslips ?? emptyObject;

//#region state.entities.betslips
type EntitiesBetslipsSelectorProps = {
  betslipId?: number;
  id?: number;
};

export const selectEntitiesBetslipsEntity: Selector<BetslipState | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => {
  const id = getBetslipIdFromProps(state, props);

  if (id) {
    return selectEntitiesBetslips(state)[id];
  }
};
//#endregion

//#region state.entities.betslips[betslipId]
export const selectEntitiesBetslipsEntityBetslipId: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.betslipId;

export const selectEntitiesBetslipsEntityCashoutValue: Selector<number | null | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.cashoutValue;

export const selectEntitiesBetslipsEntityCashoutAvailable: Selector<boolean> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.cashoutAvailable ?? false;

export const selectEntitiesBetslipsEntityBetslipReference: Selector<string | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.betslipReference;

export const selectEntitiesBetslipsEntityStatus: Selector<BetslipStatusType | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.status;

export const selectEntitiesBetslipsEntityType: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.type;

export const selectEntitiesBetslipsEntityBetAmount: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.betAmount;

export const selectEntitiesBetslipsEntityWinningsAmount: Selector<number | null | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.winningsAmount;

export const selectEntitiesBetslipsEntityWinningsBonus: Selector<number | null | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.winningsBonus;

export const selectEntitiesBetslipsEntityPossibleWinnings: Selector<number | null | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.possibleWinnings;

export const selectEntitiesBetslipsEntityPossibleBonus: Selector<number | null | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.possibleBonus;

export const selectEntitiesBetslipsEntityGlobalOdds: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.globalOdds;

export const selectEntitiesBetslipsEntityPromotionCodeId: Selector<string | null | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.promotionCodeId;

export const selectEntitiesBetslipsEntityPromotionTitle: Selector<string | null | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.promotionTitle;

export const selectEntitiesBetslipsEntityCashoutAllowed: Selector<boolean> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.cashoutAllowed ?? false;

export const selectEntitiesBetslipsEntityCompleteBetAllowed: Selector<boolean> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.completeBetAllowed ?? false;

export const selectEntitiesBetslipsEntityDetails: Selector<
  | Array<{
      outcomeId: number;
      odds: number;
    }>
  | undefined
> = (state: StateType, props: EntitiesBetslipsSelectorProps = {}) =>
  selectEntitiesBetslipsEntity(state, props)?.details;

export const selectEntitiesBetslipsEntityValidationDate: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.validationDate;

export const selectEntitiesBetslipsEntityPendingCashout: Selector<boolean> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.pendingCashout ?? false;

export const selectEntitiesBetslipsEntityAamsTicketId: Selector<string | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.aamsTicketId;

export const selectEntitiesBetslipsEntityFreebet: Selector<boolean> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.freebet ?? false;

export const selectEntitiesBetslipsEntityCnp: Selector<Array<unknown> | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.cnp;

export const selectEntitiesBetslipsEntityVoidReason: Selector<unknown> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.voidReason;

export const selectEntitiesBetslipsEntityBankers: Selector<Array<unknown> | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.bankers;

export const selectEntitiesBetslipsEntityData: Selector<BetslipStateData | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntity(state, props)?.data;
//#endregion

//#region state.entities.betslips[betslipId].data
export const selectEntitiesBetslipsEntityDataMONOPOLY: Selector<
  | {
      familyId: string;
      cardIndex: number;
    }
  | undefined
> = (state: StateType, props: EntitiesBetslipsSelectorProps = {}) =>
  selectEntitiesBetslipsEntityData(state, props)?.MONOPOLY;
//#endregion

//#region state.entities.betslips[betslipId].data.MONOPOLY
export const selectEntitiesBetslipsEntityDataMONOPOLYFamilyId: Selector<string | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntityDataMONOPOLY(state, props)?.familyId;

export const selectEntitiesBetslipsEntityDataMONOPOLYCardIndex: Selector<number | undefined> = (
  state: StateType,
  props: EntitiesBetslipsSelectorProps = {},
) => selectEntitiesBetslipsEntityDataMONOPOLY(state, props)?.cardIndex;
//#endregion
